import { Component, type ErrorInfo, type ReactNode } from 'react';

interface ErrorBoundaryState {
  error: Error | null;
}

/**
 * FR-WEB-012 — top-level React error boundary.
 *
 * Wraps <App /> in main.tsx. Without it, an uncaught render error (a
 * `undefined.map is not a function` in some deep page component, a
 * misshapen prop, a bad `useEffect` teardown) unmounts the whole tree
 * and leaves the user staring at a white page with no way to recover
 * short of a hard refresh. Worse, the ops team blames the network
 * because a white screen is indistinguishable from an outage.
 *
 * The boundary catches the throw, renders a friendly fallback with a
 * "Reload page" button, and logs the error to the console so browser
 * devtools carry the stack for support. If Sentry / equivalent is
 * added later, this is the single hook site (`componentDidCatch`).
 *
 * Sub-tree boundaries can be added around risky panels (rich text
 * editors, embedded iframes, chart libraries) for finer-grained
 * recovery. This one is the app-wide safety net.
 */
export class ErrorBoundary extends Component<{ children: ReactNode }, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Preserve the stack for support. Browser devtools + any prod
    // console-shipping tool (Sentry, LogRocket) picks this up. The
    // React componentStack is separate from the JS stack and often
    // more useful — includes the tree of components that led here.
    console.error('[error-boundary]', error, info.componentStack);
  }

  handleReload = (): void => {
    window.location.reload();
  };

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '2rem',
          fontFamily: 'system-ui, -apple-system, "Segoe UI", sans-serif',
          background: '#fafafa',
          color: '#111',
        }}
      >
        <div style={{ maxWidth: 480, width: '100%', textAlign: 'center' }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>⚠️</div>
          <h1 style={{ fontSize: 20, fontWeight: 600, marginBottom: 8 }}>
            Something went wrong.
          </h1>
          <p style={{ fontSize: 14, color: '#555', marginBottom: 24 }}>
            OTP Shield ran into an unexpected error and can't continue on this
            page. Your data is safe. Reload to try again.
          </p>
          <button
            type="button"
            onClick={this.handleReload}
            style={{
              padding: '10px 20px',
              fontSize: 14,
              fontWeight: 500,
              background: '#4f7cf7',
              color: '#fff',
              border: 0,
              borderRadius: 6,
              cursor: 'pointer',
            }}
          >
            Reload page
          </button>
          <details
            style={{
              marginTop: 32,
              fontSize: 12,
              color: '#888',
              textAlign: 'left',
            }}
          >
            <summary style={{ cursor: 'pointer', userSelect: 'none' }}>
              Error details (for support)
            </summary>
            <pre
              style={{
                marginTop: 12,
                padding: 12,
                background: '#fff',
                border: '1px solid #eee',
                borderRadius: 4,
                overflow: 'auto',
                fontSize: 11,
                lineHeight: 1.4,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {error.name}: {error.message}
              {error.stack ? `\n\n${error.stack}` : ''}
            </pre>
          </details>
        </div>
      </div>
    );
  }
}
